import styled from 'styled-components';

import HoverCopyableField from 'dashboard/components/HoverCopyableField';
import Permissioned from 'dashboard/components/Permissioned';
import { MonoSpan } from 'dashboard/components/Typography';
import TableLayout, { TableLinkTR, TableTD, TableTH } from 'dashboard/layouts/TableLayout';
import { TableNullState } from 'dashboard/layouts/TableLayoutV2';
import { formatDate } from 'dashboard/lib/utils/formatters';
import { AccountType } from 'dashboard/models/account-type';
import Spacer from 'design-system/components/Spacer';
import NullStateAccountTypes from 'images/dashboard/i16-briefcase.svg';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';
import { Permission } from 'lib/permissions';

const CenteredText = styled.div`
  text-align: center;
`;

interface AccountTypesTableProps {
  loading: boolean;
  accountTypes: AccountType[];
}

export default function AccountTypesTable({ loading, accountTypes = [] }: AccountTypesTableProps) {
  const accountTypeRows = accountTypes.map((accountType) => {
    return <AccountTableRow key={accountType.id} accountType={accountType} />;
  });

  return (
    <TableLayout
      dataTest='account-types-table__table'
      isLoading={loading}
      isEmpty={accountTypes.length === 0}
      showHeader={accountTypes.length > 0}
      headerTR={
        <>
          <TableTH
            id='account-types-table__header--account-type'
            data-test='account-types-table__header--account-type'
          >
            Name
          </TableTH>
          <TableTH id='account-types-table__header--id' data-test='account-types-table__header--id'>
            ID
          </TableTH>
          <TableTH
            id='account-types-table__header--created-at'
            data-test='account-types-table__header--created-at'
          >
            Last Updated (UTC)
          </TableTH>
        </>
      }
      nullState={
        <TableNullState dataTestBlock='account-types-view'>
          <NullStateAccountTypes />
          <Spacer height={3} />
          <Permissioned
            permission={Permission.AccountTypeView}
            fallback={
              <CenteredText>
                User permission required to view account type details. Please contact your account
                admin if you&#39;d like to view account types.
              </CenteredText>
            }
          >
            <CenteredText>Account types mirror the accounts in your system.</CenteredText>
          </Permissioned>
        </TableNullState>
      }
    >
      {accountTypeRows}
    </TableLayout>
  );
}

interface AccountTableRowProps {
  accountType: AccountType;
}

const AccountTableRow = ({ accountType }: AccountTableRowProps) => {
  const { 'updated-at': updatedAt, name: accountTypeName } = accountType.attributes;

  return (
    <TableLinkTR
      to={`/dashboard/account-types/${accountType.id}`}
      data-test={`account-type-row__${accountType.id}`}
      id={`account-type-table-row__${accountType.id}`}
    >
      <TableTD>{accountTypeName}</TableTD>
      <TableTD>
        <HoverCopyableField copyValue={accountType.id} confirmationText='Copied Account Type ID'>
          <MonoSpan>{accountType.id}</MonoSpan>
        </HoverCopyableField>
      </TableTD>
      <TableTD>{formatDate(updatedAt, DATE_AND_TIME_FORMAT)}</TableTD>
    </TableLinkTR>
  );
};
